/**
 * KeepSoul · Logo
 * Brand System 2026 — Ember edition (theme-adaptive).
 *
 * Mark: escutcheon shield with a pointed bottom and a heart inside.
 * Wordmark: "Keep" + italic "S" + "oul." in Cormorant Garamond.
 *
 * Props:
 *  - size: pixel height of the mark (default 32)
 *  - withText: render the wordmark next to the mark (default true)
 *  - withTagline: render "KEEP WHAT MATTERS MOST" under the wordmark
 *  - tone: "auto" (default — follows current theme via CSS vars) |
 *          "ember" | "bone" | "ink" — forces a specific color
 */
const TONES = {
    ember: { stroke: "#C9A36A", fill: "#C9A36A" },
    bone:  { stroke: "#F2EDE4", fill: "#F2EDE4" },
    ink:   { stroke: "#0A0A0A", fill: "#0A0A0A" },
};

function ShieldHeart({ size = 32, tone = "ember" }) {
    const { stroke, fill } = TONES[tone] || TONES.ember;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            width={size}
            height={size}
            aria-hidden="true"
            focusable="false"
            style={{ display: "block" }}
        >
            <path
                d="M14 10 H50 V36 L32 56 L14 36 Z"
                fill="none"
                stroke={stroke}
                strokeWidth="2.4"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <path
                d="M32 38 C24 33 25 25 28.4 25 C30.4 25 31.5 26.3 32 28 C32.5 26.3 33.6 25 35.6 25 C39 25 40 33 32 38 Z"
                fill={fill}
            />
        </svg>
    );
}

export default function Logo({
    size = 32,
    withText = true,
    withTagline = false,
    tone = "auto",
    className = "",
}) {
    // In "auto" mode the wordmark follows --text-primary (Bone on dark, Onyx
    // on light). Only the dot stays ember to preserve brand recognition.
    const isForced = tone !== "auto";
    const wordStyle = isForced
        ? { color: tone === "ink" ? "#0A0A0A" : "#F2EDE4" }
        : { color: "var(--text-primary)" };
    const dotColor = isForced && tone === "ink" ? "#0A0A0A" : "#C9A36A";
    const tagStyle = isForced
        ? { color: tone === "ink" ? "#2A2826" : "#8C857A" }
        : { color: "var(--text-secondary)" };
    const markTone = isForced ? tone : "ember";

    return (
        <div className={`inline-flex items-center gap-3 ${className}`} data-testid="brand-logo">
            <ShieldHeart size={size} tone={markTone} />
            {withText && (
                <div className="flex flex-col leading-none">
                    <span
                        className="font-display"
                        style={{
                            fontSize: `${size * 0.78}px`,
                            letterSpacing: "-0.015em",
                            fontWeight: 500,
                            ...wordStyle,
                        }}
                    >
                        Keep<span style={{ fontStyle: "italic" }}>S</span>oul
                        <span style={{ color: dotColor }}>.</span>
                    </span>
                    {withTagline && (
                        <span
                            className="font-label mt-2"
                            style={{
                                fontSize: `${size * 0.22}px`,
                                letterSpacing: "0.3em",
                                ...tagStyle,
                            }}
                        >
                            KEEP WHAT MATTERS MOST
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export { ShieldHeart };
